import {
    ILoanSummaryDashboardOutstandingPOCAggregate,
    ILoanSummaryDashboardOutstandingPOCPivotSummary,
} from '@apply/api/src/api/loanApi';
import { BandGrid } from '@apply/components';
import React from 'react';
import { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import { Toolbar } from './components/Toolbar/Toolbar';
import { BandInfo } from '@apply/components/src/components/controls/StandardGrid/BandGrid';
import { Tooltip } from 'devextreme-react/tooltip';
import { getDashboardLoanTypeName } from '../../../constants/getDashboardLoanTypeName';
import { DashboardOutstandingByPOCPivotMode } from '../../../constants/DashboardOutstandingByPOCPivotMode';
import { ILoanTypeOutstandingTotals } from '../../../interfaces/ILoanTypeOutstandingTotals';

export const DashboardOutstandingByPersonnel: React.FC<{
    pivotMode: DashboardOutstandingByPOCPivotMode;
    set_pivotMode: (mode: DashboardOutstandingByPOCPivotMode) => void;
    dataSource?: ILoanSummaryDashboardOutstandingPOCPivotSummary[];
    loanTypeOustandingTotals?: ILoanTypeOutstandingTotals;
}> = ({ pivotMode, set_pivotMode, dataSource = [], loanTypeOustandingTotals }) => {
    // Build BandInfo from the POC columns in the first row
    const bandInfo: BandInfo[] = React.useMemo(() => {
        if (!dataSource || dataSource.length === 0) return [];

        const firstRow = dataSource[0];
        if (!firstRow.pocColumns) return [];

        const map = new Map<number, BandInfo>();

        firstRow.pocColumns.forEach((col) => {
            const gid = col.groupId ?? 9999; // fallback group
            if (!map.has(gid)) {
                map.set(gid, {
                    id: gid,
                    display: `Group ${gid}`,
                    bandId: gid,
                    members: [],
                });
            }

            map.get(gid)!.members.push(col.pointOfContact);
        });

        return Array.from(map.values()).sort((a, b) => a.bandId - b.bandId);
    }, [dataSource]);

    const columnValueFormat = pivotMode == DashboardOutstandingByPOCPivotMode.Number ? undefined : 'currency';

    const onPocHeaderCellRender = (contact: ILoanSummaryDashboardOutstandingPOCAggregate) => {
        const targetId = `pocOutstandingHeaderCell_${contact.pointOfContact}`;
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {contact.pointOfContactName}
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {contact.pointOfContactFullName}
                </Tooltip>
            </>
        );
    };

    return (
        <div
            id='dashboardOutstandingByPersonnelGrid'
            className='loanSummaryDashboard dashboardOutstandingByPersonnelGrid'
        >
            <Toolbar
                pivotMode={pivotMode}
                set_pivotMode={set_pivotMode}
            />

            <BandGrid
                dataSource={dataSource}
                keyExpr='loanTypeId'
                defaultPageSize={10}
                pagerVisible={false}
                showBorders={true}
                showRowLines={true}
                remoteOperations={false}
                bandInfo={bandInfo}
                noDataText='No Items Found'
            >
                <Column
                    dataField='loanTypeId'
                    caption=''
                    width={140}
                    alignment='left'
                    cellRender={(e) => getDashboardLoanTypeName(e.data.loanTypeId)}
                    fixed={true}
                />
                {dataSource[0]?.pocColumns.map((c, idx) => {
                    return (
                        <Column
                            dataField={c.pointOfContact}
                            key={c.pointOfContact}
                            caption={c.pointOfContactName}
                            width={155}
                            alignment='left'
                            format={columnValueFormat}
                            headerCellRender={() => onPocHeaderCellRender(c)}
                            calculateCellValue={(row) => {
                                const poc = row.pocColumns[idx];
                                switch (pivotMode) {
                                    case DashboardOutstandingByPOCPivotMode.Committed:
                                        return poc.committed;
                                    case DashboardOutstandingByPOCPivotMode.Funded:
                                        return poc.funded;
                                    case DashboardOutstandingByPOCPivotMode.Number:
                                        return poc.count;
                                    default:
                                        return null;
                                }
                            }}
                        />
                    );
                })}

                <Summary>
                    <TotalItem
                        column='loanTypeId'
                        summaryType='count'
                        displayFormat='Total'
                        showInColumn='loanTypeId'
                    />
                    {dataSource[0]?.pocColumns.map((c, idx) => (
                        <TotalItem
                            key={c.pointOfContact}
                            summaryType='sum'
                            displayFormat='{0}'
                            column={c.pointOfContact}
                            valueFormat={columnValueFormat}
                            customizeText={(e) => {
                                const total = e.value; // numeric sum
                                const formattedTotal = e.valueText; // formatted sum

                                if (total == null) return '';

                                let pct = 0;

                                if (loanTypeOustandingTotals) {
                                    switch (pivotMode) {
                                        case DashboardOutstandingByPOCPivotMode.Committed:
                                            pct = loanTypeOustandingTotals.committed
                                                ? (total / loanTypeOustandingTotals.committed) * 100
                                                : 0;
                                            break;

                                        case DashboardOutstandingByPOCPivotMode.Funded:
                                            pct = loanTypeOustandingTotals.funded
                                                ? (total / loanTypeOustandingTotals.funded) * 100
                                                : 0;
                                            break;

                                        case DashboardOutstandingByPOCPivotMode.Number:
                                            pct = loanTypeOustandingTotals.number
                                                ? (total / loanTypeOustandingTotals.number) * 100
                                                : 0;
                                            break;
                                    }
                                }

                                const pctRounded = Number(pct.toFixed(1)); // true rounding to 1 decimal
                                const pctText =
                                    pctRounded % 1 === 0
                                        ? pctRounded.toString() // drop .0
                                        : pctRounded.toFixed(1); // keep one decimal

                                return `${formattedTotal} \u2003${pctText}%`;
                            }}
                        />
                    ))}
                </Summary>
            </BandGrid>
        </div>
    );
};
